import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Grid } from "@mui/material";
import ActionEdit from "assets/img/svg/actionEdit";
import ActionFile from "assets/img/svg/actionFile";
import ActionSend from "assets/img/svg/actionSend";
import AddDocument from "components/shared/AddDocument";
import { displayMobileNavBtn } from "components/shared/BPMobileNavBtn";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import QuickAction from "components/shared/QuickAction";
import StatementStatusChip from "components/shared/StatementStatusChip";
import RenderIf from "helpers/RenderIf";
import { formatLocaleDateString, formatNumber, renderHello } from "helpers/utils";
import type { Currencies } from "store/settingsStore";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { toggleTheme } from "hooks/useTheme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getCompagnies } from "store/beneficiaryStore";
import { sendDocumentsBP } from "store/documentStore";
import { toggleTitleDrawer } from "store/settingsStore";
import { fetchLastStatements } from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitlesByBeneficiar } from "store/titleStore";
import type { IBeneficiariesCompanies } from "store/types";

const BPDashboard = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthenticator((context) => [context.user]);
    const userName = user.attributes?.name || "";
    const dispatch = useAppDispatch();
    const { isPhone, isMobile } = useIsMobile();
    const isPWA = useIsPWA();
    const { beneficiarTitles } = useAppSelector((state) => state.titles);
    const { lastStatements } = useAppSelector((state) => state.statements);
    const { beneficiarCompagnies, beneficiarPersonalInfo } = useAppSelector((state) => state.beneficiary);
    const { getBPCompanySettings } = useBPCompanySettings({
        portalType: "beneficiary",
    });

    const mainBeneficiaryPortalType = beneficiarCompagnies[0]?.publisherType;

    const usesAgreementsOnly = getBPCompanySettings("uses_agreements_only", +beneficiarPersonalInfo.companyId!);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [chosenCompanies, setChosenCompanies] = useState<IBeneficiariesCompanies[]>([]);

    const sortTitle = (a: any, b: any) => {
        const adate = new Date(a.releaseDate);
        const bdate = new Date(b.releaseDate);

        return adate > bdate ? -1 : adate < bdate ? 1 : 0;
    };

    useEffect(() => {
        dispatch(
            fetchTitlesByBeneficiar({
                limit: 3,
                sortBy: "releaseDate",
                sortOrder: "DESC",
            }),
        );
        dispatch(fetchLastStatements());
        dispatch(getCompagnies());
    }, []);

    const lastStatementsColumns: LastElementTableColumns[] = [
        { id: "avatarUrl", label: "", hidden: true },
        { id: "name", label: t("BPDashboard.From"), align: "left" },
        {
            id: "netAmount",
            label: t("BPDashboard.NetAmount"),
            align: "right",
            format: (value: any) => (
                <span style={{ color: "var(--foreground)" }}>
                    {formatNumber(value / 100, {
                        isCurrency: true,
                        always2Digit: true,
                        preferredCurrency: beneficiarCompagnies[0]?.currency as Currencies,
                    })}{" "}
                </span>
            ),
        },
        {
            id: "isPeriodActive",
            label: t("BPDashboard.Status"),
            align: "center",
            hidden: isPhone || !lastStatements.some((item) => "isPeriodActive" in item),
            format: (value: any) => <StatementStatusChip status={value} />,
        },
    ];

    const lastTitlesColumns: LastElementTableColumns[] = [
        { id: "avatarUrl", label: "", hidden: true },
        { id: "name", label: t(`BPDashboard.Title_${mainBeneficiaryPortalType}`), align: "left" },
        {
            id: "price",
            label: t("BPDashboard.Price"),
            align: "right",
            hidden: isPhone,
            format: (value: any) =>
                formatNumber(value / 100, {
                    isCurrency: true,
                    always2Digit: true,
                    preferredCurrency: beneficiarCompagnies[0]?.currency as Currencies,
                }),
        },
        {
            id: "releaseDate",
            label: t("BPDashboard.ReleaseDate"),
            align: "right",
            format: (value: any) =>
                value ? (
                    <span style={{ color: "var(--foreground)" }}>
                        {formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })}
                    </span>
                ) : (
                    "-"
                ),
        },
    ];

    const handleFileAdd = (file: { formData: FormData }) => {
        dispatch(
            sendDocumentsBP({
                formData: file.formData,
                companyId: chosenCompanies.map((c) => `${c.id}`),
            }),
        );
    };

    const showAllStatements = () => {
        navigate("/statements");
        if (isMobile) toggleTheme("default");
    };

    const openStatement = (termId: number, periodId: number, type: string) => {
        navigate(`/statements`, { state: { termId, periodId, type } });
        if (isMobile) toggleTheme("default");
    };

    const showAllTitles = () => {
        navigate("/titles");
        if (isMobile) toggleTheme("default");
    };

    const openTitle = (id: number) => {
        navigate(`/titles/${id}`);
        if (isMobile) toggleTheme("light-grey");
        setTimeout(() => {
            dispatch(toggleTitleDrawer(true));
        }, 200);
    };

    return (
        <div className="BPDashboard" style={isPWA ? { paddingBottom: "6.5rem" } : {}}>
            <AddDocument
                isModalOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onFileAdd={handleFileAdd}
                pickerCompany
                multipleCompany
                companies={beneficiarCompagnies}
                chosenCompanies={chosenCompanies}
                setChosenCompanies={setChosenCompanies}
            />
            <div className="bpdashboard-content-top">
                <div className="bpdashboard-title">
                    {t(renderHello())} {userName.split(" ")[0]}
                </div>
                <div className="bpdashboard-subtitle">{t("BPDashboard.Subtitle")}</div>
            </div>
            <div className="bpdashboard-content-middle">
                <Grid container spacing={{ xs: 5, sm: 5, md: 3 }}>
                    <RenderIf show={!usesAgreementsOnly}>
                        <Grid item xs={12} md={12} lg={6}>
                            <div className="bpdashboard-last-payment">
                                <div className="bpdashboard-label">{t("BPDashboard.LastPayments")}</div>
                                <LastElementTable
                                    columns={lastStatementsColumns}
                                    data={lastStatements}
                                    onShowAll={showAllStatements}
                                    type={t("BPDashboard.SeeAllPayment")}
                                    onRowClick={openStatement}
                                    onRowClickId="termId"
                                    onRowClickId2="periodId"
                                    dataType="statements_bportal"
                                />
                            </div>
                        </Grid>
                    </RenderIf>
                    <Grid item xs={12} md={12} lg={usesAgreementsOnly ? 12 : 6}>
                        <div className="bpdashboard-last-title">
                            <div className="bpdashboard-label">
                                {t(`BPDashboard.LastTitles_${mainBeneficiaryPortalType}`)}
                            </div>
                            <LastElementTable
                                columns={lastTitlesColumns}
                                data={[...beneficiarTitles].sort(sortTitle)}
                                onShowAll={showAllTitles}
                                type={t(`BPDashboard.SeeAllTitle_${mainBeneficiaryPortalType}`)}
                                onRowClick={openTitle}
                                dataType="titles_bportal"
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>
            <div className="bpdashboard-content-bottom">
                <div className="bpdashboard-label">{t("BPDashboard.QuickActions")}</div>
                <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                    <Grid item xs={12} sm={6} md={usesAgreementsOnly ? 6 : 4}>
                        <QuickAction
                            text={t("BPDashboard.TransmitDocument")}
                            icon={<ActionSend />}
                            onClick={() => {
                                setIsModalOpen(true);
                                displayMobileNavBtn(false);
                            }}
                        />
                    </Grid>
                    <RenderIf show={!usesAgreementsOnly}>
                        <Grid item xs={12} sm={6} md={4}>
                            <QuickAction
                                text={t("BPDashboard.SeeStatement")}
                                icon={<ActionFile />}
                                onClick={showAllStatements}
                            />
                        </Grid>
                    </RenderIf>
                    <Grid item xs={12} sm={12} md={usesAgreementsOnly ? 6 : 4}>
                        <QuickAction
                            text={t("BPDashboard.EditPersonalInfo")}
                            icon={<ActionEdit />}
                            onClick={() => navigate("/settings")}
                        />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default BPDashboard;
